/* DocUploadField — drag-drop / browse uploader for KYB & branding docs.
 *
 * Talks to /api/seller/kyb-files/{upload,list,delete,serve}.
 *
 * Props:
 *   docType   — one of ein_letter, w9, articles, bank_statement, logo
 *   accept    — input "accept" attribute (e.g. "application/pdf" or "image/*,application/pdf")
 *   acceptLbl — short human label for the accepted formats (e.g. "PDF" / "PNG, JPG, PDF")
 *   multiple  — when true, additional uploads are appended (bank statements);
 *               when false, a new upload replaces the previous one.
 *   testIdPrefix — base for data-testid attributes.
 *   onChange  — called with the latest list of files for this doc_type.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, X, Loader2, ExternalLink, Plus } from "lucide-react";

const fmtBytes = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// Build the auth-bearing URL the browser can hit directly (used for logo
// previews and "Open" links). We rely on the /api/...?auth= query-param
// fallback the backend honours.
const authUrl = (servedUrl) => {
  if (!servedUrl) return "";
  const tok = localStorage.getItem("token") || "";
  const sep = servedUrl.includes("?") ? "&" : "?";
  return `${apiBase}${servedUrl}${sep}auth=${encodeURIComponent(tok)}`;
};

export default function DocUploadField({
  docType,
  accept = "application/pdf",
  acceptLbl = "PDF",
  multiple = false,
  testIdPrefix,
  onChange,
  extraFormFields = null, // optional dict of additional form fields (e.g. {expires_at: "2027-04-12"})
  onUploaded = null,       // optional callback fired after a successful upload
}) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // Stash `onChange` in a ref so we don't have to take a stable identity
  // dependency on the prop. Without this, consumers that pass a fresh
  // inline arrow (`onChange={() => reload()}`) would cause `refresh` to
  // get a new identity on every render, which makes the mount-effect
  // re-fire on every render and creates an infinite refresh loop that
  // also obliterates any sibling form state (e.g. the BrandingPanel
  // brand-name input).
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/seller/kyb-files");
      const list = (data?.by_type?.[docType] || []);
      setFiles(list);
      onChangeRef.current?.(list);
    } catch {
      /* ignore — empty state shown */
    }
  }, [docType]);

  useEffect(() => { refresh(); }, [refresh]);

  const upload = async (file) => {
    if (!file) return;
    // Soft warning for JPG logos — JPG can't carry an alpha channel so
    // the upload will always have whatever background it was saved with
    // (usually white). PNG is the correct format for logos that need to
    // sit on different background colors across the platform.
    if (docType === "logo" && /jpe?g/i.test(file.type || "")) {
      toast.warning(
        "JPG can't have a transparent background. Your logo will show with a white box around it on dark surfaces. Re-export as PNG with transparency for the cleanest look.",
        { duration: 7000 },
      );
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      if (extraFormFields) {
        Object.entries(extraFormFields).forEach(([k, v]) => {
          if (v !== null && v !== undefined && v !== "") fd.append(k, String(v));
        });
      }
      await api.post("/seller/kyb-files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${file.name} uploaded`);
      await refresh();
      onUploaded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) void upload(f);
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this file?")) return;
    try {
      await api.delete(`/seller/kyb-files/${id}`);
      toast.success("Removed");
      await refresh();
      onUploaded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const showDropzone = multiple || files.length === 0;

  return (
    <div className="space-y-2.5" data-testid={`${testIdPrefix}-uploader`}>
      {/* Existing uploaded files */}
      {files.length > 0 && (
        <ul className="space-y-1.5">
          {files.map((f) => (
            <li
              key={f.id}
              className="flex items-center gap-2 rounded-md border border-foreground/10 bg-secondary/40 px-3 py-2 text-[13px]"
              data-testid={`${testIdPrefix}-file-${f.id}`}
            >
              {(f.content_type || "").startsWith("image/") ? (
                <img
                  src={authUrl(f.served_url)}
                  alt=""
                  className="w-7 h-7 rounded object-cover border border-foreground/10 flex-shrink-0"
                />
              ) : (
                <FileText className="w-4 h-4 text-foreground/55 flex-shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{f.original_filename}</div>
                <div className="text-foreground/50 text-[11.5px]">
                  {fmtBytes(f.size)} · uploaded {new Date(f.created_at).toLocaleDateString()}
                  {f.expires_at && (
                    <span className={`ml-2 chip text-[10px] ${
                      new Date(f.expires_at) < new Date()
                        ? "bg-rose-50 text-rose-700 border border-rose-200"
                        : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    }`} data-testid={`${testIdPrefix}-expiry-${f.id}`}>
                      {new Date(f.expires_at) < new Date() ? "Expired " : "Expires "}
                      {new Date(f.expires_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              <a
                href={authUrl(f.served_url)}
                target="_blank"
                rel="noreferrer"
                className="text-foreground/55 hover:text-foreground p-1.5 rounded hover:bg-foreground/5"
                data-testid={`${testIdPrefix}-open-${f.id}`}
                title="Open"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <button
                type="button"
                onClick={() => remove(f.id)}
                className="text-foreground/55 hover:text-rose-600 p-1.5 rounded hover:bg-rose-50"
                data-testid={`${testIdPrefix}-remove-${f.id}`}
                title="Remove"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Drop zone / browse button */}
      {showDropzone && (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`group cursor-pointer rounded-md border-2 border-dashed px-4 py-4 transition-colors flex items-center gap-3 ${
            dragOver ? "border-foreground/60 bg-foreground/5" : "border-foreground/15 hover:border-foreground/35 hover:bg-foreground/[0.02]"
          }`}
          role="button"
          tabIndex={0}
          data-testid={`${testIdPrefix}-dropzone`}
        >
          <span className="w-9 h-9 rounded-md bg-foreground/5 grid place-items-center text-foreground/55 group-hover:text-foreground transition-colors flex-shrink-0">
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : multiple && files.length > 0 ? (
              <Plus className="w-4 h-4" />
            ) : accept.includes("image") ? (
              <ImageIcon className="w-4 h-4" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
          </span>
          <div className="text-[13px] leading-tight">
            <div className="font-medium">
              {uploading
                ? "Uploading…"
                : multiple && files.length > 0
                ? "Add another file"
                : files.length > 0
                ? "Replace this file"
                : "Click to upload or drag & drop"}
            </div>
            <div className="text-foreground/55 text-[11.5px] mt-0.5">{acceptLbl} · up to 25 MB</div>
          </div>
        </div>
      )}

      {!showDropzone && !uploading && (
        <Button
          type="button"
          onClick={() => inputRef.current?.click()}
          variant="outline"
          size="sm"
          className="rounded-full text-[12.5px]"
          data-testid={`${testIdPrefix}-replace`}
        >
          <Upload className="w-3.5 h-3.5 mr-1.5" />
          Replace file
        </Button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid={`${testIdPrefix}-input`}
      />
    </div>
  );
}
